import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

export const getUserId = () => {
    let id = localStorage.getItem("sdx_user_id");
    if (!id) {
        id = `guest_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem("sdx_user_id", id);
    }
    return id;
};

export const endpoints = {
    stats: () => api.get("/stats").then((r) => r.data),
    continents: () => api.get("/continents").then((r) => r.data),
    countries: (params = {}) => api.get("/countries", { params }).then((r) => r.data),
    categories: (country_id) => api.get("/categories", { params: { country_id } }).then((r) => r.data),
    materials: (category_id) => api.get("/materials", { params: { category_id } }).then((r) => r.data),
    materialTypes: (material_id) => api.get("/material-types", { params: { material_id } }).then((r) => r.data),
    processes: (material_type_id) => api.get("/processes", { params: { material_type_id } }).then((r) => r.data),
    process: (id) => api.get(`/processes/${id}`).then((r) => r.data),
    // New parameter architecture
    conceptsForProcess: (process_id) => api.get(`/processes/${process_id}/parameter-concepts`).then((r) => r.data),
    concept: (id) => api.get(`/parameter-concepts/${id}`).then((r) => r.data),
    conceptDatasets: async (id, params = {}) => {
        const entries = [];
        const limit = 100;
        for (let offset = 0; ; offset += limit) {
            const { data } = await api.get(`/parameter-concepts/${id}/datasets`, { params: { ...params, limit, offset } });
            entries.push(...data);
            if (data.length < limit) return entries;
        }
    },
    conceptConsensus: (id) => api.get(`/parameter-concepts/${id}/consensus`).then((r) => r.data),
    conceptCompare: (id, ids) => api.get(`/parameter-concepts/${id}/compare`, { params: { ids: ids.join(",") } }).then((r) => r.data),
    communityDataset: (id) => api.get(`/community-datasets/${id}`).then((r) => r.data),
    organization: (name) => api.get(`/organizations/${encodeURIComponent(name)}`).then((r) => r.data),
    // Community
    versions: (dataset_id) => api.get("/versions", { params: { dataset_id } }).then((r) => r.data),
    comments: (target_id, target_type) => api.get("/comments", { params: { target_id, target_type } }).then((r) => r.data),
    addComment: (payload) => api.post("/comments", payload).then((r) => r.data),
    toggleVote: (payload) => api.post("/votes/toggle", payload, {headers:{Authorization:`Bearer ${adminToken.get()}`}}).then((r) => r.data),
    myVote: (target_id, target_type, user_id) => api.get("/votes/mine", { params: { target_id, target_type }, headers:{Authorization:`Bearer ${adminToken.get()}`} }).then((r) => r.data),
    // Submissions
    previewUrl: (url) => api.post("/submissions/preview", { url }).then((r) => r.data),
    submit: (payload) => api.post("/submissions", payload).then((r) => r.data),
    search: (q, options = {}) => api.get("/search", { params: { q, ...options } }).then((r) => r.data),
    searchFacets: () => api.get("/search/facets").then(r => r.data),
    searchChildren: (kind, id, options = {}) => api.get("/search/children", { params: { ...options, kind, id } }).then((r) => r.data),
    resolveSearchResult: (kind, id) =>
        api.get("/search/resolve", { params: { kind, id } }).then((r) => r.data),
    // Wizard (guided contribution)
    wizardAddCountry: (payload) =>
        api.post("/wizard/countries", payload).then((r) => r.data),
    wizardAddCategory: (payload) =>
        api.post("/wizard/categories", payload).then((r) => r.data),
    wizardAddMaterial: (payload) =>
        api.post("/wizard/materials", payload).then((r) => r.data),
    wizardAddMaterialType: (payload) =>
        api.post("/wizard/material-types", payload).then((r) => r.data),
    wizardAddProcess: (payload) =>
        api.post("/wizard/processes", payload).then((r) => r.data),
    wizardAddParameter: (payload) =>
        api.post("/wizard/parameter-concepts", payload).then((r) => r.data),
    wizardDuplicateCheck: (payload) =>
        api.post("/wizard/duplicate-check", payload).then((r) => r.data),
    wizardSubmitDataset: (payload) =>
        api.post("/wizard/community-datasets", payload).then((r) => r.data),
    wizardBatchUpload: (file, token = "") => {
        return api.post("/wizard/batch-community-datasets", file, {
            headers: {
                "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "X-Filename": file.name,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        }).then((r) => r.data);
    },
    // Admin
    adminLogin: (payload) =>
        api.post("/admin/login", payload).then((r) => r.data),
    adminMe: (token) =>
        api.get("/admin/me", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.data),
    adminPending: (token) =>
        api.get("/admin/pending", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.data),
    adminReview: (token, payload) =>
        api.post("/admin/review", payload,
                 { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.data),
    adminReviewAll: (token, kind) =>
        api.post("/admin/review-all", null,
                 { params: { kind }, headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.data),
};

export const adminToken = {
    get: () => localStorage.getItem("sdx_admin_token") || "",
    set: (t) => localStorage.setItem("sdx_admin_token", t),
    clear: () => localStorage.removeItem("sdx_admin_token"),
};
